import { DateTime } from "luxon";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

import PopOutBox from "../shared/PopOutBox";
import classNames from "../../utils/classNames";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import { DateTimeInput } from "../shared/Inputs/DateTimeInput";
import AiConditionGenerator from "./AiConditionGenerator";
import { EbayCategoryDropdown } from "./EbayCategoryDropdown";
import { EbayConditionDropdown } from "./EbayConditionDropdown";
import { EbayStoreCategoryDropdown } from "./EbayStoreCategoryDropdown";
import { FormatType, ListingDuration } from "../../models/EbayOffer";
import { IPart } from "../../models/Part";
import useNotification from "../notifications/useNotifications";
import useTemplateConditionDescription, { CONDITION_DESCRIPTION_MAX_LENGTH } from "../../data/useTemplateConditionDescription";
import { appendToConditionDescription } from "../../utils/conditionDescription";

export const formatOptions = [
  { id: FormatType.Auction, name: "Auction" },
  { id: FormatType.FixedPrice, name: "Buy It Now" },
];

export const durationOptions = [
  { id: ListingDuration.Days1, name: "1 Day" },
  { id: ListingDuration.Days3, name: "3 Days" },
  { id: ListingDuration.Days5, name: "5 Days" },
  { id: ListingDuration.Days7, name: "7 Days" },
  { id: ListingDuration.Days10, name: "10 Days" },
  { id: ListingDuration.Days21, name: "21 Days" },
  { id: ListingDuration.Days30, name: "30 Days" },
];

type EditListingCoreFieldsProps = {
  part: IPart;
  register: any;
  getValues: any;
  setValue: any;
  watch: any;
  handleSetFormat: (_: string, value: any) => void;
  defaultPartCategory: any;
  policies: any;
  locations: any;
  bestOfferValue: boolean | null;
  isPublished: boolean;
};

export default function EditListingCoreFields({
  part,
  register,
  getValues,
  setValue,
  watch,
  handleSetFormat,
  defaultPartCategory,
  policies,
  locations,
  bestOfferValue,
  isPublished,
}: EditListingCoreFieldsProps) {
  const { renderConditionDescription } = useTemplateConditionDescription(part, false);
  const { addNotification } = useNotification();

  const conditionDescriptionLength = (watch("conditionDescription") ?? "").length;

  // For notes or a grade added after the offer was made - the box is only filled from the template
  // when it is empty, so this is the way to pick them up.
  const handleReapplyTemplate = () => {
    const current = (getValues("conditionDescription") ?? "").trim();
    if (current && !window.confirm("Replace the condition description with your Condition Description template?")) return;

    renderConditionDescription.mutate(part.id, {
      onSuccess: (response) => {
        if (response.success && response.data != null) {
          setValue("conditionDescription", response.data, { shouldDirty: true });
        } else {
          addNotification({ variant: "error", primaryText: response.message ?? "Unable to apply the condition description template" });
        }
      },
      onError: (error: any) => {
        addNotification({ variant: "error", primaryText: error?.message ?? "Unable to apply the condition description template" });
      },
    });
  };

  // The AI text goes on the end of what is in the box rather than replacing a template's sentence.
  // False tells the generator it wasn't used, so it keeps the suggestion on screen.
  const handleApplyAiCondition = (aiConditionDescription: string) => {
    const result = appendToConditionDescription(getValues("conditionDescription"), aiConditionDescription);

    if (result.error !== undefined) {
      addNotification({ variant: "warn", primaryText: result.error });
      return false;
    }

    setValue("conditionDescription", result.text, { shouldDirty: true });
    return true;
  };

  return (
    <>
      <SelectInput
        label={"Listing Format"}
        register={register}
        registerName={"format"}
        options={formatOptions}
        setValue={handleSetFormat}
        watch={watch}
      />

      {getValues("format") === FormatType.Auction ? (
        <SelectInput
          label={"Duration"}
          register={register}
          registerName={"duration"}
          options={durationOptions}
          setValue={setValue}
          watch={watch}
        />
      ) : (
        <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
      )}

      <div className="sm:col-span-1 flex gap-x-2">
        <div className="flex items-center">
          <input
            id="listNow"
            {...register("listNow")}
            type="checkbox"
            className="mr-2 h-5 w-5"
            disabled={part.soldDate !== null}
          />
          <label htmlFor="listNow">List Now</label>
        </div>

        {!watch("listNow") && (
          <div className="flex-1">
            <DateTimeInput
              label={"Listing Start"}
              register={register}
              registerName={"listingStarted"}
              min={DateTime.now().toFormat("yyyy-MM-dd'T'HH:mm")}
            />
          </div>
        )}
      </div>

      <EbayCategoryDropdown
        categoryId={part.offers.length > 0 ? part.offers[0]?.categoryId : defaultPartCategory.data?.data?.ebayCategoryId}
        register={register}
        registerName={"categoryId"}
        setValue={setValue}
        watch={watch}
      />

      <EbayStoreCategoryDropdown
        categoryId={part.offers.length > 0 ? part.offers[0]?.storeCategoryId : defaultPartCategory.data?.data?.ebayStoreCategoryId}
        register={register}
        registerName={"storeCategoryId"}
        setValue={setValue}
        watch={watch}
      />

      <div>
        {/* spacer */}
      </div>

      {getValues("format") === FormatType.Auction ? (
        <>
          <NumberInput
            label={"Starting Price (£)"}
            register={register}
            registerName={"auctionStartPrice"}
          />
          <NumberInput
            label={"Reserve Price (£)"}
            register={register}
            registerName={"auctionReservePrice"}
          />
        </>
      ) : (
        <>
          <NumberInput
            label={"Buy It Now Price (£)"}
            register={register}
            registerName={"buyItNowPrice"}
          />
          {bestOfferValue ? (
            <NumberInput
              label={"Best Offer Auto Accept Price (£)"}
              register={register}
              registerName={"autoAcceptPrice"}
            />
          ) : (
            <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
          )}
        </>
      )}

      {getValues("format") === FormatType.FixedPrice && (
        <>
          <div className="sm:col-span-1 mt-4 flex gap-x-2">
            <input
              id="enableBestOffer"
              {...register("enableBestOffer")}
              type="checkbox"
              className={classNames((part.soldDate !== null || isPublished) && "text-gray-400", "h-5 w-5")}
              disabled={part.soldDate !== null || isPublished}
            />
            <label htmlFor="enableBestOffer">Allow Best Offer?</label>
            {isPublished && (
              <PopOutBox classes="w-96 z-30">
                <p>Ebay does not allow the Best Offer setting to be changed after a listing has been published.</p>
                <p>If you want to change this setting, you will need to withdraw the listing and create a new one.</p>
              </PopOutBox>
            )}
          </div>

          <div className="sm:col-span-1 h-16">{/* Spacer */}</div>
        </>
      )}

      <SelectInput
        label={"Shipping Policy*"}
        register={register}
        registerName={"fulfillmentPolicyId"}
        options={policies.data?.data.fulfilmentPolicies}
        setValue={setValue}
        watch={watch}
      />
      <SelectInput
        label={"Payment Policy*"}
        register={register}
        registerName={"paymentPolicyId"}
        options={policies.data?.data.paymentPolicies}
        setValue={setValue}
        watch={watch}
      />
      <SelectInput
        label={"Returns Policy*"}
        register={register}
        registerName={"returnPolicyId"}
        options={policies.data?.data.returnPolicies}
        setValue={setValue}
        watch={watch}
      />

      <SelectInput
        label={"Location of Part*"}
        register={register}
        registerName={"locationKey"}
        options={locations.data?.data.locations}
        setValue={setValue}
        watch={watch}
      />

      {getValues("categoryId") ? (
        <EbayConditionDropdown
          categoryId={getValues("categoryId")}
          register={register}
          registerName={"condition"}
          setValue={setValue}
          watch={watch}
        />
      ) : (
        <div className="flex flex-col">
          <label className="mb-2 text-sm font-medium text-gray-700">
            Condition*
          </label>
          <select className="text-sm bg-gray-400" value={1} disabled >
            <option value={1} disabled>Pick a Category</option>
          </select>
        </div>
      )}

      {watch("condition") < 2000 ? null :
        <div className="flex flex-col">
          <TextInput
            register={register}
            registerName={"conditionDescription"}
            label={"Condition Description*"}
            richText
            rows={1}
            maxLength={CONDITION_DESCRIPTION_MAX_LENGTH}
          />
          <p className={classNames(
            "mt-1 text-right text-[11px]",
            conditionDescriptionLength >= CONDITION_DESCRIPTION_MAX_LENGTH ? "font-semibold text-red-600" : "text-gray-500"
          )}>
            {conditionDescriptionLength.toLocaleString()} / {CONDITION_DESCRIPTION_MAX_LENGTH.toLocaleString()}
          </p>
          <div className="mt-1.5 flex flex-wrap items-start gap-2">
            <button
              type="button"
              onClick={handleReapplyTemplate}
              disabled={renderConditionDescription.isLoading}
              title="Fill the box from your Condition Description template (Admin - Template Editor)"
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-60"
            >
              <ArrowPathIcon className={classNames("h-3.5 w-3.5", renderConditionDescription.isLoading && "animate-spin")} />
              Re-apply template
            </button>
            {/* Not gated on isPublished: unlike Best Offer, eBay lets the condition description
                be revised on a live listing, and the field above stays editable. */}
            <AiConditionGenerator
              part={part}
              onApply={handleApplyAiCondition}
            />
          </div>
        </div>
      }
    </>
  );
}
